import type { SessionRecord } from '../types';
import { fmtClock } from './metrics';

// Export = a single self-contained HTML file with the transcripts, metrics, and
// the answer audio embedded as data URIs. It opens in any browser and replays
// without the app — the "replayable, self-contained" contract from the handoff.

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

export async function exportSessionHTML(rec: SessionRecord): Promise<Blob> {
  const rows: string[] = [];
  for (const it of rec.items) {
    const audio = it.audio_blob ? await blobToDataURL(it.audio_blob) : '';
    const m = it.metrics;
    const chips = m
      ? `<span>${m.wpm} wpm</span><span>${m.filler_count} filler${m.filler_count === 1 ? '' : 's'}</span>` +
        `<span>${m.response_latency_s}s to start</span><span>${Math.round(m.talk_time_s)}s</span>`
      : `<span>transcript unavailable</span>`;
    rows.push(`
      <div class="qa">
        <div class="q">${esc(it.question_text)}</div>
        ${audio ? `<audio controls preload="none" src="${audio}"></audio>` : '<p class="none">No recording.</p>'}
        <p class="t">${it.transcript ? esc(it.transcript) : '<span class="none">Not transcribed.</span>'}</p>
        <div class="chips">${chips}</div>
      </div>`);
  }

  const s = rec.summary;
  const summary = s
    ? `<div class="sum">
         <div><b>${s.avg_wpm}</b> avg wpm</div>
         <div><b>${s.total_fillers}</b> fillers</div>
         <div><b>${s.avg_response_latency_s}s</b> avg to start</div>
         <div><b>${fmtClock(s.total_talk_time_s)}</b> talk time</div>
       </div>`
    : '';

  const html = `<!doctype html><html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>IELTS session — ${esc(rec.session_id)}</title>
<style>
  body{font:15px/1.5 -apple-system,system-ui,sans-serif;color:#1B2A2E;background:#EEF0EE;margin:0;padding:32px 20px;}
  .wrap{max-width:720px;margin:0 auto}
  h1{font-size:24px;margin:0 0 4px}
  .meta{color:#6B7A76;font-size:13px;margin-bottom:22px;font-family:ui-monospace,monospace}
  .sum{display:flex;gap:22px;flex-wrap:wrap;background:#fff;border:1px solid #D8DBD5;border-radius:10px;padding:16px 18px;margin-bottom:22px;font-size:13px;color:#6B7A76}
  .sum b{color:#1B2A2E;font-size:18px;font-family:ui-monospace,monospace}
  .qa{background:#fff;border:1px solid #D8DBD5;border-radius:10px;padding:16px 18px;margin-bottom:12px}
  .q{font-weight:600;margin-bottom:10px}
  audio{width:100%;margin-bottom:10px}
  .t{margin:0 0 10px;color:#1B2A2E}
  .none{color:#6B7A76;font-style:italic}
  .chips{display:flex;gap:8px;flex-wrap:wrap;font-size:12px;color:#6B7A76;font-family:ui-monospace,monospace}
  .chips span{border:1px solid #D8DBD5;border-radius:6px;padding:2px 8px}
</style></head><body><div class="wrap">
<h1>IELTS Speaking — session</h1>
<div class="meta">${esc(rec.mode)} · ${esc(rec.accent)} · started ${esc(rec.started_at)}</div>
${summary}
${rows.join('\n')}
<p class="meta" style="margin-top:24px">Exported from IELTS Speaking Harness. Self-contained — audio is embedded.</p>
</div></body></html>`;

  return new Blob([html], { type: 'text/html' });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
